import { getApp, getApps, initializeApp } from "firebase/app";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  reauthenticateWithCredential,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
} from "firebase/auth";

export const usesIdentityPlatform =
  process.env.REACT_APP_AUTH_PROVIDER === "identity_platform";

const identityPlatformConfig = {
  apiKey: process.env.REACT_APP_GCP_IDENTITY_PLATFORM_API_KEY,
  authDomain: process.env.REACT_APP_GCP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_GCP_PROJECT_ID,
};

const getIdentityPlatformAuth = () => {
  if (!usesIdentityPlatform) return null;

  const missingConfig = Object.entries(identityPlatformConfig)
    .filter(([key, value]) => key !== "authDomain" && !value)
    .map(([key]) => key);
  if (missingConfig.length > 0) {
    throw new Error(
      `Missing Identity Platform configuration: ${missingConfig.join(", ")}`,
    );
  }

  const app = getApps().length > 0
    ? getApp()
    : initializeApp(identityPlatformConfig);
  return getAuth(app);
};

export const identityPlatformAuth = getIdentityPlatformAuth();

const persistenceReady = identityPlatformAuth
  ? setPersistence(identityPlatformAuth, browserLocalPersistence)
  : Promise.resolve();

export const waitForIdentityPlatformAuth = async () => {
  if (!identityPlatformAuth) return null;
  await persistenceReady;
  if (typeof identityPlatformAuth.authStateReady === "function") {
    await identityPlatformAuth.authStateReady();
  }
  return identityPlatformAuth.currentUser;
};

export const getIdentityPlatformIdToken = async (forceRefresh = false) => {
  const user = await waitForIdentityPlatformAuth();
  return user ? user.getIdToken(forceRefresh) : null;
};

export const signInWithIdentityPlatform = async (email, password) => {
  await persistenceReady;
  return signInWithEmailAndPassword(identityPlatformAuth, email, password);
};

export const signInWithGoogleIdentityPlatform = async () => {
  await persistenceReady;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithPopup(identityPlatformAuth, provider);
};

export const signUpWithIdentityPlatform = async ({
  email,
  password,
  firstName,
  lastName,
  screenName,
}) => {
  await persistenceReady;
  const credential = await createUserWithEmailAndPassword(
    identityPlatformAuth,
    email,
    password,
  );
  await updateProfile(credential.user, {
    displayName: screenName || `${firstName} ${lastName}`.trim(),
  });
  await sendEmailVerification(credential.user, {
    url: `${window.location.origin}/signup?verified=1`,
  });
  return credential.user;
};

export const resendIdentityPlatformVerification = async () => {
  const user = await waitForIdentityPlatformAuth();
  if (!user) throw new Error("Sign up again to resend the verification email.");
  await sendEmailVerification(user, {
    url: `${window.location.origin}/signup?verified=1`,
  });
};

export const refreshIdentityPlatformUser = async () => {
  const user = await waitForIdentityPlatformAuth();
  if (!user) return null;
  await reload(user);
  return identityPlatformAuth.currentUser;
};

export const sendIdentityPlatformPasswordReset = (email) =>
  sendPasswordResetEmail(identityPlatformAuth, email, {
    url: `${window.location.origin}/login`,
  });

export const changeIdentityPlatformPassword = async (
  currentPassword,
  newPassword,
) => {
  const user = await waitForIdentityPlatformAuth();
  if (!user?.email) throw new Error("Not authenticated.");
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
};

export const updateIdentityPlatformProfile = async (screenName) => {
  const user = await waitForIdentityPlatformAuth();
  if (!user) throw new Error("Not authenticated.");
  await updateProfile(user, { displayName: screenName });
};

export const signOutIdentityPlatform = async () => {
  if (!identityPlatformAuth) return;
  await signOut(identityPlatformAuth);
};
